/**
 * Apple In-App Purchase Service for ADE Coins
 * Uses @capgo/native-purchases (StoreKit 2) for native iOS
 * Falls back to Stripe for web
 */
import { Capacitor } from "@capacitor/core";

// Apple IAP Product IDs — must match App Store Connect configuration
export const IAP_PRODUCTS = [
  { id: "ade_coins_500",    coins: 500,    price: 5.99,   name: "500 ADE Coins" },
  { id: "ade_coins_1000",   coins: 1000,   price: 11.99,  name: "1.000 ADE Coins" },
  { id: "ade_coins_2000",   coins: 2000,   price: 22.99,  name: "2.000 ADE Coins" },
  { id: "ade_coins_3000",   coins: 3000,   price: 32.99,  name: "3.000 ADE Coins" },
  { id: "ade_coins_5000",   coins: 5000,   price: 49.99,  name: "5.000 ADE Coins" },
  { id: "ade_coins_10000",  coins: 10000,  price: 94.99,  name: "10.000 ADE Coins" },
  { id: "ade_coins_20000",  coins: 20000,  price: 179.99, name: "20.000 ADE Coins" },
  { id: "ade_coins_50000",  coins: 50000,  price: 429.99, name: "50.000 ADE Coins" },
  { id: "ade_coins_100000", coins: 100000, price: 849.99, name: "100.000 ADE Coins" },
  { id: "ade_coins_500000", coins: 500000, price: 3999.99, name: "500.000 ADE Coins" },
];

class AppleIAPService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.initialized = false;
    this.products = [];
    this.purchaseListeners = [];
  }

  /**
   * Check if we're running on native iOS (App Store build)
   */
  isAvailable() {
    return this.isNative && Capacitor.getPlatform() === "ios";
  }

  /**
   * Initialize the IAP service and load products from App Store
   */
  async initialize() {
    if (!this.isAvailable() || this.initialized) return;

    try {
      const { NativePurchases } = await import("@capgo/native-purchases");

      // Register products
      const productIds = IAP_PRODUCTS.map((p) => p.id);
      const result = await NativePurchases.getProducts({ productIds });
      this.products = result.products || [];
      this.initialized = true;

      // Listen for purchase updates
      NativePurchases.addListener("purchaseUpdate", (purchase) => {
        this.purchaseListeners.forEach((fn) => fn(purchase));
      });

      console.log(`IAP initialized: ${this.products.length} products loaded`);
      return this.products;
    } catch (err) {
      console.warn("IAP init failed:", err);
      return [];
    }
  }

  /**
   * Get loaded products with Apple prices
   */
  getProducts() {
    if (!this.isAvailable() || !this.initialized) {
      // Return default products for web/preview
      return IAP_PRODUCTS.map((p) => ({
        ...p,
        priceString: `€${p.price.toFixed(2)}`,
        productId: p.id,
      }));
    }

    return this.products.map((appleProduct) => {
      const local = IAP_PRODUCTS.find((p) => p.id === appleProduct.productId);
      return {
        ...local,
        productId: appleProduct.productId,
        priceString: appleProduct.priceString || `€${local?.price?.toFixed(2) || "0.00"}`,
        applePrice: appleProduct.price,
        title: appleProduct.title || local?.name,
        description: appleProduct.description,
      };
    });
  }

  /**
   * Purchase a product
   * @returns {Promise<{success: boolean, transactionId?: string, receipt?: string}>}
   */
  async purchase(productId) {
    if (!this.isAvailable()) {
      throw new Error("IAP not available — use Stripe on web");
    }

    try {
      const { NativePurchases } = await import("@capgo/native-purchases");
      const result = await NativePurchases.purchaseProduct({ productId });

      if (result.transactionId) {
        return {
          success: true,
          transactionId: result.transactionId,
          receipt: result.jwsRepresentation || result.receipt,
          productId,
        };
      }

      return { success: false, error: "Purchase cancelled" };
    } catch (err) {
      if (err.code === "USER_CANCELLED" || err.message?.includes("cancel")) {
        return { success: false, error: "cancelled" };
      }
      throw err;
    }
  }

  /**
   * Restore previous purchases (required by Apple)
   */
  async restorePurchases() {
    if (!this.isAvailable()) return [];

    try {
      const { NativePurchases } = await import("@capgo/native-purchases");
      const result = await NativePurchases.restorePurchases();
      return result.purchases || [];
    } catch (err) {
      console.warn("Restore failed:", err);
      return [];
    }
  }

  /**
   * Add a listener for purchase completions
   */
  onPurchaseComplete(callback) {
    this.purchaseListeners.push(callback);
    return () => {
      this.purchaseListeners = this.purchaseListeners.filter((fn) => fn !== callback);
    };
  }
}

// Singleton
let instance = null;
export const getIAPService = () => {
  if (!instance) instance = new AppleIAPService();
  return instance;
};

export default AppleIAPService;
